import type { CalendarEvent } from './contracts';
import { Temporal } from '@js-temporal/polyfill';

function eventInstant(value: string, timezone: string) {
  if (/[zZ]|[+-]\d\d:\d\d$/.test(value)) return new Date(value);
  return new Date(
    Number(Temporal.PlainDateTime.from(value).toZonedDateTime(timezone).epochMilliseconds),
  );
}

function eventEndInstant(value: string, timezone: string) {
  if (/[zZ]|[+-]\d\d:\d\d$/.test(value)) return Temporal.Instant.from(value);
  return Temporal.PlainDateTime.from(value).toZonedDateTime(timezone).toInstant();
}

export function eventHasEnded(
  event: Pick<CalendarEvent, 'end' | 'allDay' | 'timezone'>,
  now = Temporal.Now.instant(),
) {
  if (event.allDay) {
    const exclusiveEnd = Temporal.PlainDate.from(event.end.slice(0, 10));
    const today = now.toZonedDateTimeISO(event.timezone).toPlainDate();
    return Temporal.PlainDate.compare(exclusiveEnd, today) <= 0;
  }
  return Temporal.Instant.compare(eventEndInstant(event.end, event.timezone), now) <= 0;
}

export function formatEventRange(
  event: Pick<CalendarEvent, 'start' | 'end' | 'allDay' | 'timezone'>,
) {
  const dateOptions = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  } as const;
  if (event.allDay) {
    const start = Temporal.PlainDate.from(event.start.slice(0, 10));
    const exclusiveEnd = Temporal.PlainDate.from(event.end.slice(0, 10));
    if (Temporal.PlainDate.compare(start, exclusiveEnd) >= 0)
      throw new RangeError('End must follow start');
    const lastDay = exclusiveEnd.subtract({ days: 1 });
    const format = (date: Temporal.PlainDate) =>
      new Intl.DateTimeFormat('en-US', {
        ...dateOptions,
        timeZone: 'UTC',
      }).format(new Date(Date.UTC(date.year, date.month - 1, date.day, 12)));
    return start.equals(lastDay) ? format(start) : `${format(start)} - ${format(lastDay)}`;
  }
  const start = eventInstant(event.start, event.timezone);
  const end = eventInstant(event.end, event.timezone);
  if (!(end.getTime() > start.getTime())) throw new RangeError('End must follow start');
  const day = new Intl.DateTimeFormat('en-US', { ...dateOptions, timeZone: event.timezone });
  const zone = new Intl.DateTimeFormat('en-US', {
    timeZone: event.timezone,
    timeZoneName: 'short',
  });
  const zoneName = (date: Date) =>
    zone.formatToParts(date).find((part) => part.type === 'timeZoneName')?.value;
  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: event.timezone,
    ...(zoneName(start) !== zoneName(end) ? { timeZoneName: 'short' as const } : {}),
  });
  const startDay = day.format(start),
    endDay = day.format(end);
  return `${startDay}, ${time.format(start)} - ${startDay === endDay ? '' : `${endDay}, `}${time.format(end)}`;
}
